import {InvoicePrintItem, InvoicePrintModel, InvoicePrintParty} from './_models'
import {PrintColumn} from './printColumns'
import {formatNumber, hasAmount, sumNumbers} from './printNumbers'
import {PagePart} from './printPagination'
import {persianText} from './printText'

// What the sheets show besides the rows, derived from the printed invoice. Pure, so it is tested
// without rendering.

export type IdentityField = {
  key: string
  title: string
  value: string
  /** code: read left to right in the Latin font when it has letters (tax IDs). */
  format: 'code' | 'text' | 'date'
  wide?: boolean
  /** A mark after the value pointing to a notice («*» for a provisional tax ID). */
  mark?: string
}

export const PROVISIONAL_MARK = '*'

/** «پیش‌فاکتور»: an offer printed as such, never as a tax invoice. */
export const isProformaPrint = (invoice: InvoicePrintModel) =>
  invoice.status.code === 'proforma' || Boolean(invoice.proforma)

/**
 * Whether a party's box prints its name row. A buyer known only by «توضیحات در باکس خریدار» (a
 * type-2 invoice with no buyer recorded) prints that description alone, not an empty name above it.
 */
export const showsPartyName = (party: InvoicePrintParty) => Boolean(party.name) || !party.note

/** The heading of every sheet. */
export const printDocumentTitle = (invoice: InvoicePrintModel) =>
  isProformaPrint(invoice) ? 'پیش‌فاکتور' : 'صورتحساب الکترونیکی'

/** A proforma's identity: its own number, issue day and validity; no tax fields. */
const proformaIdentityFields = (invoice: InvoicePrintModel): IdentityField[] => {
  const fields: IdentityField[] = []
  if (invoice.invoiceNumber) {
    fields.push({
      key: 'no',
      title: 'شماره پیش‌فاکتور',
      value: invoice.invoiceNumber,
      format: 'code',
    })
  }
  fields.push({key: 'indatim', title: 'تاریخ صدور', value: invoice.issuedAt, format: 'date'})
  if (invoice.proforma?.validUntil) {
    fields.push({
      key: 'validUntil',
      title: 'معتبر تا',
      value: invoice.proforma.validUntil,
      format: 'date',
    })
  }
  fields.push({key: 'inp', title: 'الگو', value: patternLabel(invoice), format: 'text'})
  fields.push({
    key: 'tracking',
    title: 'کد پیگیری سامانه',
    value: invoice.trackingCode,
    format: 'code',
  })
  return fields
}

export const identityFields = (invoice: InvoicePrintModel): IdentityField[] => {
  if (isProformaPrint(invoice)) {
    return proformaIdentityFields(invoice)
  }

  const fields: IdentityField[] = []
  if (invoice.taxId) {
    fields.push({
      key: 'taxid',
      title: 'شماره منحصر به فرد مالیاتی',
      value: invoice.taxId,
      format: 'code',
      wide: true,
      mark: invoice.status.taxIdFinal ? undefined : PROVISIONAL_MARK,
    })
  }
  fields.push({key: 'indatim', title: 'تاریخ و زمان صدور', value: invoice.issuedAt, format: 'date'})
  if (invoice.invoiceNumber) {
    fields.push({key: 'no', title: 'شماره صورتحساب', value: invoice.invoiceNumber, format: 'code'})
  }
  fields.push({key: 'inty', title: 'نوع صورتحساب', value: invoice.invoiceTypeTitle, format: 'text'})
  fields.push({key: 'inp', title: 'الگوی صورتحساب', value: patternLabel(invoice), format: 'text'})
  fields.push({key: 'ins', title: 'موضوع صورتحساب', value: invoice.subjectTitle, format: 'text'})
  if (invoice.referenceTaxId) {
    fields.push({
      key: 'irtaxid',
      title: 'شماره منحصر به فرد مالیاتی صورتحساب مرجع',
      value: invoice.referenceTaxId,
      format: 'code',
      wide: true,
    })
  }
  if (invoice.internalSerial) {
    fields.push({
      key: 'inno',
      title: 'سریال صورتحساب',
      value: invoice.internalSerial,
      format: 'code',
    })
  }
  fields.push({
    key: 'tracking',
    title: 'کد پیگیری سامانه',
    value: invoice.trackingCode,
    format: 'code',
  })
  return fields
}

/** «۱۱ (بورس اوراق بهادار مبتنی بر کالا)». */
export const patternLabel = (invoice: InvoicePrintModel) =>
  `${invoice.pattern} (${invoice.patternTitle})`

export type TotalRow = {key: string; title: string; value: string; strong?: boolean}

/** The invoice totals as sent (tprdis … tbill), each only when its field is in pattern. */
export const totalRows = (invoice: InvoicePrintModel): TotalRow[] => {
  const totals = invoice.totals
  const rows: TotalRow[] = []
  const add = (
    key: string,
    title: string,
    value: string | null,
    options: {whenZero?: boolean; strong?: boolean} = {}
  ) => {
    if (value === null || value === undefined) return
    if (!options.whenZero && !hasAmount(value)) return
    rows.push({key, title, value: formatNumber(value), strong: options.strong})
  }

  add('tprdis', 'مجموع مبلغ قبل از کسر تخفیف', totals.amountBeforeDiscount, {whenZero: true})
  add('tdis', 'مجموع تخفیفات', totals.discount, {whenZero: true})
  add('tadis', 'مجموع مبلغ پس از کسر تخفیف', totals.amountAfterDiscount, {whenZero: true})
  add('tonw', 'مجموع وزن خالص', totals.netWeight)
  add('torv', 'مجموع ارزش ریالی', totals.rialValue)
  add('tocv', 'مجموع ارزش ارزی', totals.currencyValue)
  add('tvam', 'مجموع مالیات بر ارزش افزوده', totals.vat, {whenZero: true})
  add('todam', 'مجموع سایر مالیات، عوارض و وجوه قانونی', totals.otherTaxes)
  add('tax17', 'مالیات موضوع ماده ۱۷', totals.article17Tax)
  add('tbill', 'مجموع صورتحساب', totals.total, {whenZero: true, strong: true})
  return rows
}

/** Sentences under the totals: provisional tax ID, a replaced or cancelled invoice, inherited rows. */
export const notices = (invoice: InvoicePrintModel): string[] => {
  if (isProformaPrint(invoice)) {
    // A proforma's terms, what became of it, and the sentence that keeps it from passing for an invoice.
    return [
      invoice.proforma?.paymentTerms ? `شرایط پرداخت: ${invoice.proforma.paymentTerms}` : '',
      invoice.proforma?.deliveryTerms ? `شرایط تحویل: ${invoice.proforma.deliveryTerms}` : '',
      invoice.status.detail || '',
      invoice.proforma?.notice ||
        'این سند پیش‌فاکتور است و صورتحساب مالیاتی نیست؛ به سامانهٔ مؤدیان ارسال نمی‌شود و اعتبار مالیاتی ایجاد نمی‌کند.',
    ].filter(Boolean)
  }

  const result: string[] = []
  if (invoice.status.detail) {
    const provisional =
      !invoice.status.taxIdFinal &&
      invoice.taxId &&
      invoice.status.isActive &&
      invoice.status.code !== 'cancelled'
    result.push(
      provisional ? `${PROVISIONAL_MARK} ${invoice.status.detail}` : invoice.status.detail
    )
  }
  if (invoice.itemsInherited && invoice.items.length > 0) {
    result.push(
      'ردیف‌های این صورتحساب ابطالی از صورتحساب مرجع آمده‌اند و فقط برای اطلاع چاپ شده‌اند.'
    )
  }
  if (invoice.status.isSandbox) {
    result.push('این صورتحساب در محیط آزمایشی (سندباکس) صادر شده و اعتبار مالیاتی ندارد.')
  }
  return result
}

export const PROFORMA_WATERMARK = 'پیش‌فاکتور'

/** «پیش‌فاکتور» names the document (blue); every other mark says it does not hold (red). */
export const watermarkTone = (mark: string | null): 'info' | 'danger' =>
  mark === PROFORMA_WATERMARK ? 'info' : 'danger'

/**
 * The word printed across every sheet of an invoice that must not be taken for a valid one. Every
 * proforma carries «پیش‌فاکتور» (user decision 2026-10-13), or why it no longer holds.
 */
export const watermark = (invoice: InvoicePrintModel): string | null => {
  if (isProformaPrint(invoice)) {
    if (!invoice.status.isActive) return 'فاقد اعتبار'
    return invoice.proforma?.state === 'expired' ? 'منقضی‌شده' : PROFORMA_WATERMARK
  }
  if (invoice.status.code === 'superseded') return 'فاقد اعتبار'
  if (invoice.status.code === 'cancelled') return 'ابطال‌شده'
  if (invoice.status.isSandbox) return 'آزمایشی'
  return null
}

/**
 * The template's fixed text, then the invoice's own description, as printable lines (split between
 * sheets). The fixed text (account number, IBAN, the business's name…) comes first, as the earlier
 * print page always printed it; businesses write their per-invoice notes to follow it.
 */
export const noteLines = (invoice: InvoicePrintModel): string[] => {
  const lines: string[] = []
  const add = (text: string | null) => {
    if (!text) return
    persianText(text)
      .split('\n')
      .map((line) => line.trim())
      .forEach((line) => lines.push(line))
  }
  add(invoice.footerText)
  if (invoice.description && invoice.footerText) lines.push('')
  add(invoice.description)
  // Blank lines only separate paragraphs; none at the ends.
  while (lines.length && !lines[0]) lines.shift()
  while (lines.length && !lines[lines.length - 1]) lines.pop()
  return lines
}

export type RowsTotals = {
  /** Column key → sum of this sheet's rows. */
  page: Record<string, string | null>
  /** Column key → sum of every row up to the end of this sheet. */
  running: Record<string, string | null>
}

/** Page and running totals of the summable columns, for each sheet that holds rows. */
export const rowsTotalsBySheet = (
  items: InvoicePrintItem[],
  columns: PrintColumn[],
  pages: PagePart[][],
  key = 'items'
): Map<number, RowsTotals> => {
  const summable = columns.filter((column) => column.sum)
  const result = new Map<number, RowsTotals>()
  pages.forEach((page, pageIndex) => {
    page.forEach((part) => {
      if (part.kind !== 'rows' || part.key !== key) return
      const pageRows = items.slice(part.from, part.to)
      const runningRows = items.slice(0, part.to)
      const totals: RowsTotals = {page: {}, running: {}}
      summable.forEach((column) => {
        totals.page[column.key] = sumNumbers(pageRows.map(column.value))
        totals.running[column.key] = sumNumbers(runningRows.map(column.value))
      })
      result.set(pageIndex, totals)
    })
  })
  return result
}
